import { useState } from 'react';
import { useTenant } from '@/hooks/useTenant';
import { useLanguage } from '@/hooks/useLanguage';

/**
 * Botón flotante de WhatsApp.
 *
 * Se muestra abajo a la derecha en cualquier página de la landing siempre
 * que el tenant tenga configurado un número de WhatsApp (o, en su defecto,
 * un teléfono). Abre `https://wa.me/<numero>` con un mensaje prellenado.
 *
 * Al hacer hover (o tap en móvil) despliega una etiqueta con el CTA
 * "Pide información por WhatsApp".
 */
export function WhatsAppFloatingButton() {
  const { config } = useTenant();
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);

  const raw = config?.contact.whatsapp || config?.contact.phone;
  if (!raw) return null;

  // wa.me requiere el número sin '+' ni espacios ni guiones.
  const digits = raw.replace(/[^0-9]/g, '');
  if (!digits) return null;

  const tenantName = config?.name || 'CIDENAR';
  const message = encodeURIComponent(t('ui.whatsappMessage', { name: tenantName }));
  const href = `https://wa.me/${digits}?text=${message}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t('ui.whatsappCta')}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      onFocus={() => setExpanded(true)}
      onBlur={() => setExpanded(false)}
      className="fixed bottom-5 right-5 z-50 flex items-center gap-3 rounded-full bg-[#25D366] py-3 pl-3 pr-4 text-white shadow-lg shadow-black/20 ring-1 ring-black/5 transition-transform duration-200 hover:scale-105 hover:bg-[#1DA851] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#25D366] md:bottom-6 md:right-6"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 32 32"
        className="h-7 w-7 shrink-0"
        aria-hidden="true"
        fill="currentColor"
      >
        <path d="M19.11 17.205c-.372 0-.882.176-1.196.51l-2.21 2.18c-1.39-.78-2.62-1.86-3.49-3.39-.85-1.49-.93-2.95-.93-2.95s.16-.55.47-.87l1.5-1.5c.34-.34.5-.83.5-1.34v-.16c0-.27-.07-.55-.18-.78-.13-.27-.7-1.36-1.13-2.09-.18-.31-.49-.51-.83-.51h-1.71c-.37 0-.76.18-1.04.46-.86.86-1.55 1.98-1.62 3.49-.05.95.12 1.84.36 2.68 1.04 3.7 4.79 7.05 8.13 8.59.83.36 1.71.59 2.6.59 1.66 0 3.27-.6 4.31-1.65l.91-.91c.27-.27.46-.65.46-1.05v-1.62c0-.43-.27-.74-.51-.91-.72-.43-1.81-.99-2.09-1.13-.23-.11-.49-.18-.78-.18zM16 .195C7.27.195.2 7.265.2 15.995c0 2.8.74 5.42 2.04 7.69L.27 31.8l8.31-2.18A15.71 15.71 0 0 0 16 31.805c8.73 0 15.8-7.07 15.8-15.81S24.73.195 16 .195zm0 28.93c-2.5 0-4.85-.7-6.85-1.92l-.49-.29-5.07 1.33 1.36-4.94-.32-.51c-1.34-2.13-2.04-4.6-2.04-7.14C2.59 8.585 8.59 2.585 16 2.585s13.41 6 13.41 13.41-6 13.13-13.41 13.13z" />
      </svg>
      <span
        className={`overflow-hidden whitespace-nowrap text-sm font-semibold leading-tight transition-all duration-200 ${
          expanded ? 'max-w-[260px] opacity-100' : 'max-w-0 opacity-0 md:max-w-[260px] md:opacity-100'
        }`}
      >
        {t('ui.whatsappCta')}
      </span>
    </a>
  );
}

export default WhatsAppFloatingButton;
